import React, { useEffect, useState, useRef } from "react";
import {
    Modal,
    Button,
    Form,
    Input,
    Table,
    Select,
    message,
    Spin,
    Popconfirm
} from "antd";
import { EditOutlined, DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import {
    get_parameters,
    create_parameter,
    update_parameter,
    delete_parameter,
    fetch_all_labs,
    get_all_tests_labs,
    link_test_to_parameter,
    get_test_linked_parameters,
    get_all_active_parameters,
} from "../../endpoints/API";

const { Option } = Select;

const Parameter = () => {
    const [form] = Form.useForm();
    const [parameters, setParameters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);

    const [searchText, setSearchText] = useState("");
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const debounceRef = useRef(null);

    const [isLinkTestModalOpen, setIsLinkTestModalOpen] = useState(false);
    const [labs, setLabs] = useState([]);
    const [selectedLab, setSelectedLab] = useState(null);
    const [tests, setTests] = useState([]);
    const [selectedTest, setSelectedTest] = useState(null);
    const [selectedParameters, setSelectedParameters] = useState([]);
    const [allParameters, setAllParameters] = useState([]);

    const fetchParameters = async (search = "", page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const response = await get_parameters(search, page, pageSize);
            const { results, count } = response.data;
            setParameters(results || []);
            setPagination((prev) => ({ ...prev, total: count, current: page, pageSize }));
        } catch (error) {
            message.error("Failed to fetch parameters");
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchParameters();
    }, []);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            if (searchText.length === 0 || searchText.length >= 2) {
                fetchParameters(searchText, 1, pagination.pageSize);
            }
        }, 500);
        return () => clearTimeout(debounceRef.current);
    }, [searchText]);

    const handleTableChange = (pag) => {
        fetchParameters(searchText, pag.current, pag.pageSize);
    };

    const handleFormSubmit = async (values) => {
        try {
            let response;
            if (editingRecord) {
                response = await update_parameter(editingRecord.id, values);
                message.success("Parameter updated");
            } else {
                response = await create_parameter(values);
                message.success("Parameter created");
            }

            if (response.status === 200 || response.status === 201) {
                fetchParameters(searchText, pagination.current, pagination.pageSize);
                form.resetFields();
                setIsModalOpen(false);
            }
        } catch (error) {
            const errData = error?.response?.data;
            if (errData && typeof errData === "object") {
                Object.values(errData).flat().forEach((msg) => message.error(msg));
            } else {
                message.error("Something went wrong");
            }
        }
    };

    const handleEdit = (record) => {
        setEditingRecord(record);
        form.setFieldsValue(record);
        setIsModalOpen(true);
    };

    const showModal = () => {
        setEditingRecord(null);
        form.resetFields();
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        try {
            const res = await delete_parameter(id);
            if (res.status === 200) {
                message.success("Parameter archived");
                fetchParameters(searchText, pagination.current, pagination.pageSize);
            }
        } catch (error) {
            message.error(error?.response?.data?.detail || "Failed to delete");
        }
    };

    const openLinkTestModal = async () => {
        setIsLinkTestModalOpen(true);
        try {
            const [labsRes, paramsRes] = await Promise.all([
                fetch_all_labs(),
                get_all_active_parameters(),
            ]);
            setLabs(labsRes.data.labs || []);
            setAllParameters(paramsRes.data || []);
        } catch (error) {
            message.error("Failed to load data");
        }
    };

    const handleLabChange = async (labId) => {
        setSelectedLab(labId);
        setSelectedTest(null);
        try {
            const res = await get_all_tests_labs(labId);
            setTests(res.data || []);
        } catch (error) {
            message.error("Failed to fetch tests for selected lab");
            setTests([]);
        }
    };

    const handleTestChange = async (testId) => {
        setSelectedTest(testId);
        if (!testId) {
            setSelectedParameters([]);
            return;
        }
        try {
            const res = await get_test_linked_parameters(testId);
            const linkedParams = res.data.map((item) => item.parameter.id);
            setSelectedParameters(linkedParams);
        } catch (error) {
            message.error("Failed to fetch linked parameters for this test");
            setSelectedParameters([]);
        }
    };

    const handleLinkTestSubmit = async () => {
        if (!selectedLab || !selectedTest || selectedParameters.length === 0) {
            message.error("Please select lab, test, and parameters");
            return;
        }

        const successParams = [];
        const duplicateParams = [];

        for (const paramId of selectedParameters) {
            try {
                await link_test_to_parameter(selectedTest, paramId);
                successParams.push(paramId);
            } catch (error) {
                if (error?.response?.status === 400) {
                    duplicateParams.push(paramId);
                } else {
                    message.error("Failed to link some parameters");
                }
            }
        }

        if (successParams.length > 0) {
            message.success("Parameters linked successfully");
        }

        if (duplicateParams.length > 0) {
            const duplicateNames = parameters
                .filter((p) => duplicateParams.includes(p.id))
                .map((p) => p.name)
                .join(", ");
            message.warning(`Already linked: ${duplicateNames}`);
        }

        setIsLinkTestModalOpen(false);
        setSelectedLab(null);
        setSelectedTest(null);
        setSelectedParameters([]);
    };

    const columns = [
        { title: "Parameter Name", dataIndex: "name" },
        {
            title: "Action",
            render: (_, record) => (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Button onClick={() => handleEdit(record)}>
                        <EditOutlined />
                    </Button>
                    <Popconfirm
                        title="Are you sure you want to delete this parameter?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button danger>
                            <DeleteOutlined />
                        </Button>
                    </Popconfirm>
                </div>
            ),
        },
    ];

    return (
        <div style={{ padding: "16px" }}>
            {/* Header row — wraps on mobile */}
            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                    marginBottom: 16,
                    alignItems: "center",
                }}
            >
                <Input
                    prefix={<SearchOutlined />}
                    placeholder="Search parameters..."
                    style={{ width: "100%", maxWidth: 250 }}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                />
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Button type="primary" onClick={showModal}>
                        Create Parameter
                    </Button>
                    <Button onClick={openLinkTestModal}>Link Test to Parameter</Button>
                </div>
            </div>

            {/* Scrollable table wrapper */}
            <Spin spinning={loading}>
                <div style={{ overflowX: "auto", width: "100%" }}>
                    <Table
                        columns={columns}
                        dataSource={parameters}
                        rowKey={(record) => record.id}
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            showSizeChanger: true,
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: "max-content" }}
                    />
                </div>
            </Spin>

            {/* Create/Edit Parameter Modal */}
            <Modal
                title={editingRecord ? "Edit Parameter" : "Create Parameter"}
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={null}
                width="min(480px, 95vw)"
                style={{ top: 20 }}
            >
                <Form layout="vertical" form={form} onFinish={handleFormSubmit}>
                    <Form.Item
                        label="Parameter Name"
                        name="name"
                        rules={[{ required: true, message: "Please enter parameter name" }]}
                    >
                        <Input placeholder="Enter parameter name" />
                    </Form.Item>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
                        <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
                        <Button type="primary" htmlType="submit">Submit</Button>
                    </div>
                </Form>
            </Modal>

            {/* Link Test to Parameter Modal */}
            <Modal
                title="Link Test to Parameter"
                open={isLinkTestModalOpen}
                onCancel={() => setIsLinkTestModalOpen(false)}
                onOk={handleLinkTestSubmit}
                okText="Link Test"
                width="min(520px, 95vw)"
                style={{ top: 20 }}
                bodyStyle={{ maxHeight: "75vh", overflowY: "auto" }}
            >
                <Form layout="vertical">
                    <Form.Item label="Select Lab">
                        <Select
                            placeholder="Select Lab"
                            value={selectedLab}
                            onChange={handleLabChange}
                            showSearch
                            style={{ width: "100%" }}
                        >
                            {labs.map((lab) => (
                                <Option key={lab.id} value={lab.id}>
                                    {lab.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {selectedLab && (
                        <>
                            <Form.Item label="Select Test">
                                <Select
                                    placeholder="Select Test"
                                    value={selectedTest}
                                    onChange={handleTestChange}
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().includes(input.toLowerCase())
                                    }
                                    style={{ width: "100%" }}
                                >
                                    {tests.map((test) => (
                                        <Option key={test.id} value={test.id}>
                                            {test.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item label="Select Parameters">
                                <Select
                                    mode="multiple"
                                    placeholder="Search & select parameters"
                                    value={selectedParameters}
                                    onChange={setSelectedParameters}
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().includes(input.toLowerCase())
                                    }
                                    style={{ width: "100%" }}
                                >
                                    {allParameters.map((param) => (
                                        <Option key={param.id} value={param.id}>
                                            {param.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </>
                    )}
                </Form>
            </Modal>
        </div>
    );
};

export default Parameter;